// ---------------------------------------------------------------------------
// Restoring the remembered workspace onto the URL (ENG-725).
//
// Explicit URLs carry the workspace (/personal/projects or
// /team/<slug>/projects), and the navbar chip mirrors them. But the app also
// needs entry URLs that resume where you left off: the desktop app and auth
// callback both open bare /projects. The remembered workspace then lives only
// in the store until this module restores it onto an explicit URL.
//
// This is the missing direction: on a bare workspace URL, take the remembered
// context and put it back into the route. Pure so the decision table is
// testable without a router.
// ---------------------------------------------------------------------------

import type { WorkspaceSurface } from './workspaceRoutes'

export type { WorkspaceSurface } from './workspaceRoutes'

export type WorkspaceRestoreDecision =
  /** Nothing to restore — the URL already names the right workspace. */
  | { kind: 'idle' }
  /** Can't tell yet; hold the page's loading gate instead of flashing personal. */
  | { kind: 'wait' }
  /** Send the browser to the remembered team's equivalent surface. */
  | { kind: 'navigate'; href: string }
  /** The remembered team is gone — forget it so the chip stops naming it. */
  | { kind: 'reset' }
  /**
   * Memberships are unavailable, so the remembered team can't be checked.
   * Render personal, but leave the stored context alone — including the
   * surface's own mirror-the-URL write, which would otherwise overwrite it.
   */
  | { kind: 'unresolved' }

interface WorkspaceRestoreInput {
  /** False when the URL already names a workspace (or the surface opts out). */
  enabled: boolean
  /** True once the persisted context has been applied to the store. */
  hydrated: boolean
  /** 'personal' or a team org id. */
  contextId: string
  surface: WorkspaceSurface
  /** null while memberships are unknown (loading, or the fetch failed). */
  teamOrgs: readonly { id: string; slug: string | null }[] | null
  membershipsLoading: boolean
  /** Current query string, with or without the leading '?'. Carried across. */
  search?: string
}

export function decideWorkspaceRestore({
  enabled,
  hydrated,
  contextId,
  surface,
  teamOrgs,
  membershipsLoading,
  search = '',
}: WorkspaceRestoreInput): WorkspaceRestoreDecision {
  if (!enabled) return { kind: 'idle' }
  // Pre-hydration the store always reads 'personal' (SSR parity), which is
  // indistinguishable from "personal is the remembered workspace" — wait.
  if (!hydrated) return { kind: 'wait' }
  if (contextId === 'personal') return { kind: 'idle' }

  if (teamOrgs === null) {
    // Still fetching → wait. Failed → render personal (the chip falls back to
    // "Personal" on an empty team list anyway) but leave the remembered
    // context alone: a transient network error shouldn't erase which workspace
    // you were in.
    return membershipsLoading ? { kind: 'wait' } : { kind: 'unresolved' }
  }

  const team = teamOrgs.find(t => t.id === contextId)
  // A team you've left or that was deleted, or one with no slug to route to:
  // either way there's no URL for it, so stop remembering it.
  if (!team?.slug) return { kind: 'reset' }

  // Carry the query across: the two surfaces are the same page, so anything
  // addressed by a param belongs to the destination too.
  const query = search.replace(/^\?/, '')
  return { kind: 'navigate', href: `/team/${team.slug}/${surface}${query ? `?${query}` : ''}` }
}

/** True while the surface should hold its loading gate rather than paint personal. */
export function isRestoringWorkspace(decision: WorkspaceRestoreDecision): boolean {
  return decision.kind === 'wait' || decision.kind === 'navigate'
}

/**
 * True while the surface must NOT mirror its URL into the workspace context —
 * either the restore is still in flight, or it couldn't be evaluated and the
 * stored workspace has to survive untouched.
 */
export function holdsContextSync(decision: WorkspaceRestoreDecision): boolean {
  return isRestoringWorkspace(decision) || decision.kind === 'unresolved'
}
